import React, { memo, useCallback, useMemo, useState } from 'react'
import { Image, ImageStyle, SectionList, useWindowDimensions, View } from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { CommonRefreshControl } from '@src/components'
import {
  ArcIndicatorsPager,
  CalendarBar,
  StatCard,
  ValueColorLegend,
} from '@src/screens/Insights/components'
import { useFetchStats } from '@src/screens/Insights/utils/hooks'
import { NotificationBanner } from '@src/components/notifications/NotificationBanner'
import { LiveSurfaceBanner } from '@src/components/notifications/banners/LiveSurfaceBanner'
import { MobileAppFeature, NotificationEngineScreen, Statistic, UiStateNames } from '@src/types'
import { User } from '@src/utils'
import { primaryGoalMetricSelector, primaryGoalSelector, userSelector } from '@src/selectors/app'
import { Text } from '@src/components/base'
import { blurredArcIndicatorsImage, poweredBySteloLogoImage } from '@src/assets/images'
import { UpsellCGMBanner } from '@src/screens/Home/components/Banners/UpsellCGMBanner'
import { useIsDarkMode } from '@src/config/theme'
import { useHasStandaloneVideoCallSubscription } from '@src/hooks/useHasStandaloneVideoCallSubscription'
import { statsSettingsStateSelector } from '@src/selectors/settings'
import { Device } from '@src/config'
import { useShowDexcomSteloLogo } from '@src/utils/hooks'
import { statsSelector } from '../models/insights.selectors'
import { GoalView } from '../components/GoalView'
import { BLOOD_PRESSURE_KEYS } from '../constants'
import { getInitialSections, getKey, GLUCOSE_SECTION } from '../utils/utils'

const keyExtractor = (item: Statistic) => item.meta.key

export const InsightsContent = memo(() => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const isDarkMode = useIsDarkMode()
  const user = useSelector(userSelector)

  const hasStandaloneVideoCallSubscription = useHasStandaloneVideoCallSubscription()

  const [refreshing, setRefreshing] = useState(false)

  const { refetch, fetchStats } = useFetchStats()

  const primaryGoal = useSelector(primaryGoalSelector)

  const primaryGoalMetrics = useSelector(primaryGoalMetricSelector)

  const hasGlucoseInsights = User.hasFeature(MobileAppFeature.GlucoseInsights)

  const hasViewedTrends = user?.uiStates.some(
    ({ name, value }) => name === UiStateNames.ViewedTrends && value === true,
  )

  const shouldShowGoalView =
    primaryGoal && (hasGlucoseInsights || hasStandaloneVideoCallSubscription)

  useFocusEffect(
    useCallback(() => {
      dispatch({ type: 'users/fetch' })
      if (!hasViewedTrends) {
        dispatch({
          type: 'app/upsertUiState',
          payload: {
            name: UiStateNames.ViewedTrends,
            value: true,
          },
        })
      }
    }, [dispatch, hasViewedTrends]),
  )

  const insightsSettings = useSelector(statsSettingsStateSelector)
  const stats = useSelector(statsSelector)
  const dimensions = useWindowDimensions()
  const hasSmallScreen = Device.hasSmallScreen(dimensions)
  const showLogo = useShowDexcomSteloLogo()

  const sections = useMemo(() => {
    const statsMap = stats.reduce((acc, stat) => {
      const { key, section } = stat.meta

      if (BLOOD_PRESSURE_KEYS.includes(key)) {
        return acc
      } else {
        acc[getKey({ section, key })] = stat
      }

      return acc
    }, {} as Record<string, Statistic | undefined>)

    const sectionItems = insightsSettings.reduce((acc, item) => {
      if (!item.visible || item.section === 'score' || item.key === 'blood_pressure') {
        return acc
      }

      if (
        primaryGoalMetrics.some(
          (metric) => metric.section === item.section && metric.statKey === item.key,
        )
      ) {
        return acc
      }

      const stat = statsMap[getKey(item)]

      if (!stat) {
        return acc
      }

      if (!hasGlucoseInsights && item.section === GLUCOSE_SECTION) {
        return acc
      }

      acc[item.section].items.push(stat)

      return acc
    }, getInitialSections({ includeGlucose: hasGlucoseInsights }))
    return Object.values(sectionItems).map(({ title, items }) => ({
      title,
      data: items,
    }))
  }, [hasGlucoseInsights, insightsSettings, primaryGoalMetrics, stats])

  const renderItem = useCallback(({ item }: { item: Statistic }) => <StatCard item={item} />, [])

  const refreshControl = useMemo(() => {
    const onRefresh = async () => {
      setRefreshing(true)
      try {
        await refetch()
      } finally {
        setRefreshing(false)
      }
    }

    return <CommonRefreshControl refreshing={refreshing} onRefresh={onRefresh} />
  }, [refetch, refreshing])

  const listHeaderComponent = useMemo(() => {
    return (
      <>
        <LiveSurfaceBanner screen={NotificationEngineScreen.Insights} />
        <NotificationBanner screen={NotificationEngineScreen.Insights} />
        <CalendarBar fetchStats={fetchStats} />
        {hasGlucoseInsights ? (
          <ArcIndicatorsPager />
        ) : (
          <UpsellCGMBanner
            image={<Image source={blurredArcIndicatorsImage(isDarkMode).imageSource} />}
          />
        )}
        {shouldShowGoalView && <GoalView goal={primaryGoal} metrics={primaryGoalMetrics} />}
      </>
    )
  }, [
    fetchStats,
    hasGlucoseInsights,
    isDarkMode,
    primaryGoal,
    primaryGoalMetrics,
    shouldShowGoalView,
  ])

  const renderSectionHeader = useCallback(
    ({ section: { title } }: { section: { title: string } }) => {
      const logoComponent = (
        <View style={styles.logoContainer}>
          <Image
            source={poweredBySteloLogoImage(isDarkMode).imageSource}
            style={styles.logoStyle as ImageStyle}
          />
        </View>
      )
      return (
        <View
          style={[
            styles.sectionHeading,
            showLogo ? { flexDirection: 'column' } : { flexDirection: 'row', alignItems: 'center' },
          ]}
        >
          <Text type={hasSmallScreen ? 'small' : 'regular'} bold style={styles.sectionTitle}>
            {title}
          </Text>
          {title === sections[0].title && (
            <View style={showLogo && styles.containerWithLogo}>
              {hasGlucoseInsights && <ValueColorLegend />}
              {showLogo && logoComponent}
            </View>
          )}
        </View>
      )
    },
    [
      hasGlucoseInsights,
      hasSmallScreen,
      isDarkMode,
      sections,
      showLogo,
      styles.containerWithLogo,
      styles.logoContainer,
      styles.logoStyle,
      styles.sectionHeading,
      styles.sectionTitle,
    ],
  )

  return (
    <SectionList
      sections={sections}
      keyExtractor={keyExtractor}
      renderItem={renderItem}
      stickySectionHeadersEnabled={false}
      refreshControl={refreshControl}
      ListHeaderComponent={listHeaderComponent}
      renderSectionHeader={renderSectionHeader}
    />
  )
})

const themedStyles = StyleService.create({
  sectionHeading: {
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: 4,
    marginTop: 16,
    marginBottom: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  containerWithLogo: {
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  logoContainer: {
    justifyContent: 'center',
    paddingBottom: 12,
  },
  logoStyle: {
    width: 72,
    height: 18,
    resizeMode: 'contain',
  },
  sectionTitle: {
    color: 'theme.text.secondary',
  },
})
